import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import ProfileStep from "../components/ProfileStep";
import PricingStep from "../components/PricingStep";
import PaymentsStep from "../components/PaymentsStep";
import InviteStep from "../components/InviteStep";

import { useCoachMarkers } from "@/hooks/useCoachMarkers";
import PATHS from "@/routes/paths";
import useUserState from "@/hooks/useUserState.tsx";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import LanguageSwitcher from "@/layouts/AuthLayout/components/LanguageSwitcher/LanguageSwitcher.tsx";

const OnboardingPage = () => {
  const { t } = useTranslation(["onboarding", "common"]);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<"profile" | "pricing" | "payments" | "invite">(
    "profile",
  );
  const location = useLocation();
  const userState = useUserState();
  const { user, setShouldReload } = useUserStore();
  const { setEnabled } = useCoachMarkers();

  // Determine current step based on path and user state
  const getCurrentStep = () => {
    switch (userState) {
      case "tutorWithoutProfile":
        if (location.pathname !== PATHS.TUTOR.ONBOARDING.PROFILE)
          navigate(PATHS.TUTOR.ONBOARDING.PROFILE);

        return "profile";
      case "tutorWithoutPricing":
        if (location.pathname !== PATHS.TUTOR.ONBOARDING.PRICING)
          navigate(PATHS.TUTOR.ONBOARDING.PRICING);

        return "pricing";
      case "tutorWithoutPayments":
        if (location.pathname !== PATHS.TUTOR.ONBOARDING.PAYMENTS)
          navigate(PATHS.TUTOR.ONBOARDING.PAYMENTS);

        return "payments";
      case "tutorWithoutWorkspace":
        if (location.pathname !== PATHS.TUTOR.ONBOARDING.INVITE)
          navigate(PATHS.TUTOR.ONBOARDING.INVITE);

        return "invite";
      default:
        navigate(PATHS.REDIRECT);

        return "profile";
    }
  };

  useEffect(() => {
    setCurrentStep(getCurrentStep());
  }, [userState, location.pathname]);

  // Ensure coach markers persisted storage exists during onboarding flows
  useEffect(() => {
    try {
      const storageKey = "coach-markers-storage";

      if (typeof window !== "undefined" && !localStorage.getItem(storageKey)) {
        // Trigger a state write to create the storage object with defaults
        setEnabled(true);
      }
    } catch {
      // Fallback: attempt to write anyway
      setEnabled(true);
    }
  }, [setEnabled]);

  // Determine next step based on user's special_role_checks
  const getNextStep = () => {
    if (!user) return PATHS.TUTOR.ONBOARDING.PROFILE;

    const { specialRoleChecks } = user;

    // Check if profile is needed
    if (!specialRoleChecks.has_public_profile) {
      return PATHS.TUTOR.ONBOARDING.PROFILE;
    }

    // Check if pricing is needed
    if (!specialRoleChecks.has_default_pricing) {
      return PATHS.TUTOR.ONBOARDING.PRICING;
    }

    // Check if payments is needed
    if (!specialRoleChecks.has_payments_setup) {
      return PATHS.TUTOR.ONBOARDING.PAYMENTS;
    }

    // Check if invite help should be shown
    if (localStorage.getItem("should_show_invite_help") === "true") {
      return PATHS.TUTOR.ONBOARDING.INVITE;
    }

    // All steps completed, go to dashboard
    return PATHS.TUTOR.DASHBOARD;
  };

  const handleNext = () => {
    setShouldReload(true);
    setTimeout(() => {
      const nextStep = getNextStep();

      navigate(nextStep);
    }, 500);
  };

  const handleSkip = () => {
    // Clear localStorage and go to dashboard
    localStorage.removeItem("should_show_invite_help");
    navigate(PATHS.REDIRECT);
  };

  return (
    <div className="bg-background relative flex h-full w-full items-center justify-center overflow-hidden bg-gradient-to-br p-0 sm:p-6">
      <title>{`${t("onboarding:title", "Onboarding")} - Ximly`}</title>
      <div className={"absolute top-2 right-2 z-50"}>
        <LanguageSwitcher />
      </div>
      {/* Main Container */}
      <div className="relative z-10 flex h-full w-full max-w-7xl items-center md:pt-6 lg:pt-12">
        {currentStep === "profile" && <ProfileStep onNext={handleNext} />}
        {currentStep === "pricing" && <PricingStep onNext={handleNext} />}
        {currentStep === "payments" && <PaymentsStep onNext={handleNext} />}
        {currentStep === "invite" && <InviteStep onNext={handleNext} onSkip={handleSkip} />}
      </div>
    </div>
  );
};

export default OnboardingPage;
